// eslint-disable-next-line simple-import-sort/imports
import * as Sentry from '@sentry/react'

import { routeTree } from './routeTree.gen'

import { createRouter } from '@tanstack/react-router'

const router = createRouter({ routeTree })

const ANALYTICS_HOST_PATTERN = /google-analytics\.com|googletagmanager\.com|analytics\.google\.com/

// iOS Safari rejects blocked fetch() calls with `TypeError: Load failed` and Chromium
// browsers use `Failed to fetch`. When the Google Analytics beacon is blocked by tracking
// protection, gtag.js doesn't catch its own rejection so it surfaces as an unhandled
// rejection with no stacktrace (Sentry issue LINGO-CARDS-26).
const isBlockedFetchMessage = (message: string | undefined) =>
  message === 'Load failed' || message?.includes('Failed to fetch')

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enableLogs: true,
  denyUrls: [ANALYTICS_HOST_PATTERN],
  beforeSend(event, hint) {
    const error = hint.originalException as Error | undefined
    if (!isBlockedFetchMessage(error?.message)) return event

    const lastFetch = event.breadcrumbs?.filter(b => b.category === 'fetch').at(-1)
    const url = String(lastFetch?.data?.url ?? '')
    if (lastFetch?.data?.status_code === 0 && ANALYTICS_HOST_PATTERN.test(url)) return null

    return event
  },
  integrations: [
    // See docs for support of different versions of variation of react router
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.consoleLoggingIntegration(),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 1.0,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [import.meta.env.VITE_API_BASE, import.meta.env.VITE_EDGE_BASE],
  propagateTraceparent: true,

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
})
